/**
 * Conflict Resolution Service
 * 
 * Handles conflicts that arise when syncing offline changes with server data.
 * Provides conflict detection, resolution strategies, and conflict management.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';
import { logger } from './logger';

export type ConflictType = 'edit' | 'delete' | 'concurrent_edit';

export type Conflict = {
  id: string;
  collection: string;
  docId: string;
  localVersion: any;
  serverVersion: any;
  conflictType: ConflictType;
  detectedAt: Date;
  resolvedAt?: Date;
  resolution?: 'local' | 'server' | 'merge';
  tripId?: string;
  userId?: string;
};

export type ConflictResolution = 'local' | 'server' | 'merge';

const CONFLICTS_STORAGE_KEY = '@tripflow_conflicts';

/**
 * Detect conflicts between local and server versions
 */
export const detectConflict = (
  localVersion: any,
  serverVersion: any,
  collection: string,
  docId: string
): ConflictType | null => {
  // If local version doesn't exist but server does, it's a delete conflict
  if (!localVersion && serverVersion) {
    return 'delete';
  }

  // If server version doesn't exist but local does, no conflict (local wins)
  if (localVersion && !serverVersion) {
    return null;
  }

  // If both exist, check for concurrent edits
  if (localVersion && serverVersion) {
    // Simple conflict detection: compare last modified timestamps
    const localModified = localVersion.lastModified || localVersion.updatedAt || localVersion.createdAt;
    const serverModified = serverVersion.lastModified || serverVersion.updatedAt || serverVersion.createdAt;

    if (localModified && serverModified) {
      const localTime = localModified instanceof Date ? localModified.getTime() : new Date(localModified).getTime();
      const serverTime = serverModified instanceof Date ? serverModified.getTime() : new Date(serverModified).getTime();

      // If both were modified, it's a concurrent edit
      if (localTime !== serverTime) {
        return 'concurrent_edit';
      }
    } else {
      // If we can't determine modification time, assume concurrent edit
      return 'concurrent_edit';
    }
  }

  return null;
};

/**
 * Create a conflict record
 */
export const createConflict = async (
  collection: string,
  docId: string,
  localVersion: any,
  serverVersion: any,
  conflictType: ConflictType,
  tripId?: string,
  userId?: string
): Promise<Conflict> => {
  const conflict: Conflict = {
    id: `conflict_${collection}_${docId}_${Date.now()}`,
    collection,
    docId,
    localVersion,
    serverVersion,
    conflictType,
    detectedAt: new Date(),
    tripId,
    userId,
  };

  await saveConflict(conflict);
  logger.warn('[ConflictResolution] Conflict detected', {
    id: conflict.id,
    collection,
    docId,
    conflictType,
  });

  return conflict;
};

/**
 * Save conflict to storage
 */
const saveConflict = async (conflict: Conflict): Promise<void> => {
  try {
    const conflicts = await getConflicts();
    conflicts.push(conflict);
    await AsyncStorage.setItem(CONFLICTS_STORAGE_KEY, JSON.stringify(conflicts));
  } catch (error) {
    logger.error('[ConflictResolution] Error saving conflict', error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
};

/**
 * Get all unresolved conflicts
 */
export const getConflicts = async (): Promise<Conflict[]> => {
  try {
    const stored = await AsyncStorage.getItem(CONFLICTS_STORAGE_KEY);
    if (!stored) {
      return [];
    }

    const conflicts: Conflict[] = JSON.parse(stored);
    // Convert date strings back to Date objects
    return conflicts.map(conflict => ({
      ...conflict,
      detectedAt: new Date(conflict.detectedAt),
      resolvedAt: conflict.resolvedAt ? new Date(conflict.resolvedAt) : undefined,
    }));
  } catch (error) {
    logger.error('[ConflictResolution] Error getting conflicts', error instanceof Error ? error : new Error(String(error)));
    return [];
  }
};

/**
 * Get unresolved conflicts
 */
export const getUnresolvedConflicts = async (): Promise<Conflict[]> => {
  const conflicts = await getConflicts();
  return conflicts.filter(c => !c.resolvedAt);
};

/**
 * Get conflicts for a specific trip
 */
export const getConflictsForTrip = async (tripId: string): Promise<Conflict[]> => {
  const conflicts = await getConflicts();
  return conflicts.filter(c => c.tripId === tripId);
};

/**
 * Resolve a conflict
 */
export const resolveConflict = async (
  conflictId: string,
  resolution: ConflictResolution,
  mergedData?: any
): Promise<any> => {
  try {
    const conflicts = await getConflicts();
    const conflictIndex = conflicts.findIndex(c => c.id === conflictId);

    if (conflictIndex === -1) {
      throw new Error(`Conflict ${conflictId} not found`);
    }

    const conflict = conflicts[conflictIndex];
    
    // Determine final data based on resolution
    let finalData: any;
    switch (resolution) {
      case 'local':
        finalData = conflict.localVersion;
        break;
      case 'server':
        finalData = conflict.serverVersion;
        break;
      case 'merge':
        finalData = mergedData || mergeData(conflict.localVersion, conflict.serverVersion);
        break;
    }

    // Update conflict with resolution
    conflicts[conflictIndex] = {
      ...conflict,
      resolvedAt: new Date(),
      resolution,
    };

    await AsyncStorage.setItem(CONFLICTS_STORAGE_KEY, JSON.stringify(conflicts));

    logger.info('[ConflictResolution] Conflict resolved', {
      conflictId,
      resolution,
      collection: conflict.collection,
      docId: conflict.docId,
    });

    // Return the final data for the caller to use
    return finalData;
  } catch (error) {
    logger.error('[ConflictResolution] Error resolving conflict', error instanceof Error ? error : new Error(String(error)));
    throw error;
  }
};

/**
 * Merge local and server data (simple merge strategy)
 */
const mergeData = (local: any, server: any): any => {
  // Simple merge: prefer local for most fields, but take server timestamps
  return {
    ...server,
    ...local,
    // Preserve server timestamps
    createdAt: server.createdAt || local.createdAt,
    updatedAt: new Date(), // Current time for merged version
    // Merge arrays if they exist
    ...(local.items && server.items ? { items: [...new Set([...server.items, ...local.items])] } : {}),
  };
};

/**
 * Auto-resolve conflicts using a strategy
 */
export const autoResolveConflict = async (
  conflict: Conflict,
  strategy: 'last_write_wins' | 'local_wins' | 'server_wins' = 'last_write_wins'
): Promise<any> => {
  let resolution: ConflictResolution;
  let mergedData: any;

  switch (strategy) {
    case 'last_write_wins':
      const localTime = conflict.localVersion?.updatedAt || conflict.localVersion?.lastModified;
      const serverTime = conflict.serverVersion?.updatedAt || conflict.serverVersion?.lastModified;
      
      if (localTime && serverTime) {
        const localTimestamp = localTime instanceof Date ? localTime.getTime() : new Date(localTime).getTime();
        const serverTimestamp = serverTime instanceof Date ? serverTime.getTime() : new Date(serverTime).getTime();
        
        resolution = localTimestamp > serverTimestamp ? 'local' : 'server';
      } else {
        resolution = 'local'; // Default to local if timestamps unavailable
      }
      break;

    case 'local_wins':
      resolution = 'local';
      break;

    case 'server_wins':
      resolution = 'server';
      break;
  }

  await resolveConflict(conflict.id, resolution, mergedData);
  return resolution === 'local' ? conflict.localVersion : 
         resolution === 'server' ? conflict.serverVersion :
         mergedData;
};

/**
 * Remove resolved conflicts (cleanup)
 */
export const removeResolvedConflicts = async (olderThanDays: number = 7): Promise<number> => {
  try {
    const conflicts = await getConflicts();
    const cutoffDate = Date.now() - (olderThanDays * 24 * 60 * 60 * 1000);
    
    const unresolved = conflicts.filter(c => !c.resolvedAt || 
      (c.resolvedAt && c.resolvedAt.getTime() > cutoffDate)
    );

    const removed = conflicts.length - unresolved.length;
    await AsyncStorage.setItem(CONFLICTS_STORAGE_KEY, JSON.stringify(unresolved));

    logger.info(`[ConflictResolution] Removed ${removed} resolved conflicts`);
    return removed;
  } catch (error) {
    logger.error('[ConflictResolution] Error removing resolved conflicts', error instanceof Error ? error : new Error(String(error)));
    return 0;
  }
};

/**
 * Get conflict count
 */
export const getConflictCount = async (): Promise<number> => {
  const conflicts = await getUnresolvedConflicts();
  return conflicts.length;
};

